import { SearchClient } from "algoliasearch";
import { useFormContext } from "react-hook-form";
import {
  Divider,
  HStack,
  VStack,
  Text,
  Icon,
  Box,
  Spinner,
} from "@chakra-ui/react";
import { FiTag, FiDollarSign, FiUser } from "react-icons/fi";
import { KYDInput, KYDSelect, KYDMultiSelect } from "src/ui";
import { KYDGenresList } from "src/Manager/Genres";
import { InstantSearch } from "react-instantsearch";
import FilterItem from "./FilterItem";
import CollapsableContent from "./CollapsableContent";
import { IoPricetagsOutline } from "react-icons/io5";
import { LayoutGroup, motion } from "framer-motion";
import KYDSwitch from "src/ui/KYDSwitch";
import { CreateSegmentFormValues } from ".";
import AlgoliaMultiSelect from "./AlgoliaMultiSelect";
import actions from "src/actions";
import { useCallback, useEffect, useState } from "react";
import { KYDGender } from "@common/interfaces/KYDUser";

const CreateNewSegmentComposer = ({
  isUpdate,
  eventsAlgoliaConfig,
  segmentSize,
  segmentSizeLoading,
}: {
  isUpdate: boolean;
  eventsAlgoliaConfig: {
    indexName: string;
    searchClient: SearchClient;
  };
  segmentSize: number;
  segmentSizeLoading: boolean;
}) => {
  const form = useFormContext<CreateSegmentFormValues>();
  const [tags, setTags] = useState<{ label: string; value: string }[]>([]);

  const fetchOrganizationTags = useCallback(async () => {
    const res = await actions.ADMIN.fetchOrganizationTags();
    setTags(res.map((tag) => ({ label: tag, value: tag })));
  }, []);

  useEffect(() => {
    fetchOrganizationTags();
  }, [fetchOrganizationTags]);

  const filters = form.watch("filters");

  const hasEventBasedFilters =
    filters.genres.length > 0 ||
    filters.venues.length > 0 ||
    filters.artists.length > 0 ||
    filters.events.length > 0;

  const hasPurchaseBehaviourFilters =
    filters.average_order_value.value > 0 ||
    filters.average_items_per_order.value > 0 ||
    filters.order_count.value > 0 ||
    filters.total_attendance.value > 0;

  const hasDemographicsFilters =
    filters.age.value > 0 ||
    (filters.gender !== null && filters.gender !== "any");

  const hasCustomTagsFilters = filters.custom_tags.length > 0;

  return (
    <LayoutGroup id="segment-group">
      <motion.div layoutScroll>
        <Divider borderBottom="1px solid rgba(0, 0, 0, 0.20)" mb={6} />
        <KYDInput
          label="Segment Name"
          placeholder="Enter Segment Name"
          value={form.watch("name")}
          onChange={(e) => form.setValue("name", e.target.value)}
        />
        <Divider mt={6} borderBottom="1px solid rgba(0, 0, 0, 0.20)" mb={6} />
        <HStack px={3} justify="space-between" w="100%" alignItems="center">
          <VStack align="flex-start" gap={2}>
            <Text
              color="#1A202C"
              fontSize="20px"
              fontStyle="normal"
              fontWeight="600"
              lineHeight="120%"
            >
              Auto Update
            </Text>
            <Text
              color="#666"
              fontSize="14px"
              fontStyle="normal"
              fontWeight="400"
              lineHeight="140%"
              maxW="380px"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              Segment will auto update with new fans, that match your filter
              conditions, regularly
            </Text>
          </VStack>
          <KYDSwitch
            size="lg"
            isChecked={form.watch("type") === "dynamic"}
            onChange={(e) => {
              form.setValue("type", e.target.checked ? "dynamic" : "static");
            }}
          />
        </HStack>
        <Divider mt={6} borderBottom="1px solid rgba(0, 0, 0, 0.20)" mb={6} />
        <HStack
          px={3}
          mb={8}
          justify="space-between"
          w="100%"
          alignItems="center"
        >
          <VStack align="flex-start" gap={2}>
            <Text
              color="#1A202C"
              fontSize="20px"
              fontStyle="normal"
              fontWeight="600"
              lineHeight="120%"
            >
              Filters
            </Text>
            <Text
              color="#666"
              fontSize="14px"
              fontStyle="normal"
              fontWeight="400"
              lineHeight="140%"
              maxW="380px"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              Users will automatically enter or exist this segment as they match
              the filters you choose
            </Text>
          </VStack>
          <Box
            borderRadius="29px"
            border="1px solid rgba(18, 18, 18, 0.16)"
            color="#121212"
            display="flex"
            justifyContent="center"
            alignItems="center"
            px={3}
            py={1.5}
          >
            {segmentSizeLoading ? (
              <Spinner />
            ) : segmentSize !== null ? (
              `${segmentSize.toLocaleString()} Contacts`
            ) : (
              "- Contacts"
            )}
          </Box>
        </HStack>
        <CollapsableContent
          icon={<Icon as={FiTag} />}
          title="Events"
          isOpen={hasEventBasedFilters}
        >
          <KYDMultiSelect
            id="genre"
            label="Genre"
            placeholder="Search genres"
            options={KYDGenresList.map((item) => ({
              label: item.label,
              value: item.name,
            }))}
            values={form.watch("filters.genres")}
            onChange={(vals) => {
              form.setValue("filters.genres", vals);
            }}
          />
          <InstantSearch
            searchClient={eventsAlgoliaConfig?.searchClient}
            indexName={eventsAlgoliaConfig?.indexName}
          >
            <AlgoliaMultiSelect
              searchKey="name"
              label="Event"
              placeholder="Search events"
              values={form.watch("filters.events")}
              onChange={(vals) => {
                form.setValue("filters.events", vals);
              }}
            />
          </InstantSearch>
          <InstantSearch
            searchClient={eventsAlgoliaConfig?.searchClient}
            indexName={eventsAlgoliaConfig?.indexName}
          >
            <AlgoliaMultiSelect
              searchKey="performers.name"
              label="Artist Lineup"
              placeholder="Search artist lineup"
              values={form.watch("filters.artists")}
              onChange={(vals) => {
                form.setValue("filters.artists", vals);
              }}
            />
          </InstantSearch>
          <InstantSearch
            searchClient={eventsAlgoliaConfig?.searchClient}
            indexName={eventsAlgoliaConfig?.indexName}
          >
            <AlgoliaMultiSelect
              searchKey="venues.name"
              label="Venue"
              placeholder="Search venues"
              values={form.watch("filters.venues")}
              onChange={(vals) => {
                form.setValue("filters.venues", vals);
              }}
            />
          </InstantSearch>
        </CollapsableContent>
        {/* <CollapsableContent icon={<Icon as={FiMail} />} title="Email Stats">
          <HStack>
            <FilterItem
              label="Open Rate"
              hasRightBorder
              valuePostfix="%"
              keyName="open_rate"
              minValue={0}
              maxValue={100}
            />
            <FilterItem
              label="Click Through Rate"
              hasRightBorder
              valuePostfix="%"
              keyName="click_through_rate"
              minValue={0}
              maxValue={100}
            />
            <FilterItem
              label="Conversion Rate"
              hasRightBorder={false}
              valuePostfix="%"
              keyName="conversion_rate"
              minValue={0}
              maxValue={100}
            />
          </HStack>
        </CollapsableContent> */}
        <CollapsableContent
          icon={<Icon as={FiDollarSign} />}
          title="Purchase Behavior"
          isOpen={hasPurchaseBehaviourFilters}
        >
          <HStack alignItems="flex-end">
            <FilterItem
              label="Avg. Basket Size"
              hasRightBorder
              valuePostfix="$"
              keyName="average_order_value"
              // minValue={0}
              maxValue={10000}
            />
            <FilterItem
              label="Avg. Items Per Order"
              hasRightBorder
              valuePostfix=""
              keyName="average_items_per_order"
              // minValue={0}
              maxValue={10000}
            />
            <FilterItem
              label="How often purchased"
              hasRightBorder
              valuePostfix=""
              keyName="order_count"
              minValue={0}
              maxValue={100}
            />
            <FilterItem
              label="How often attended"
              hasRightBorder={false}
              valuePostfix=""
              keyName="total_attendance"
              minValue={0}
              maxValue={100}
            />
          </HStack>
        </CollapsableContent>
        <CollapsableContent
          icon={<Icon as={FiUser} />}
          title="Demographics Filter"
          isOpen={hasDemographicsFilters}
        >
          <HStack justify="space-between" w="100%" alignItems="center">
            <FilterItem
              minValue={0}
              maxValue={100}
              label="Age"
              hasRightBorder={true}
              valuePostfix="Yrs"
              keyName="age"
            />
            <VStack align="flex-start" gap={2}>
              <Text
                color="rgba(26, 32, 44, 0.75)"
                fontSize="14px"
                fontWeight="500"
                lineHeight="120%"
              >
                Gender
              </Text>
              <KYDSelect
                options={[
                  { label: "Male", value: "male" },
                  { label: "Female", value: "female" },
                  { label: "Other", value: "other" },
                  { label: "Any", value: "any" },
                ]}
                defaultValue={form.watch("filters.gender") || "any"}
                onChange={(e) => {
                  form.setValue("filters.gender", e.target.value as KYDGender);
                }}
              />
            </VStack>
          </HStack>
        </CollapsableContent>
        <CollapsableContent
          icon={<Icon as={IoPricetagsOutline} />}
          title="Custom Tags"
          isOpen={hasCustomTagsFilters}
        >
          <KYDMultiSelect
            id="custom-tags"
            label="Tags"
            placeholder="Search or select multiple tags"
            options={tags}
            values={form.watch("filters.custom_tags")}
            onChange={(vals) => {
              form.setValue("filters.custom_tags", vals);
            }}
          />
        </CollapsableContent>
      </motion.div>
    </LayoutGroup>
  );
};

export default CreateNewSegmentComposer;
